import React from 'react';
import { Button } from 'primereact/button';
import {
    SP_SKIP, extractResponseData, SmartStyles, SmartHeader, SmartSection, NoRecordsCard
} from './_SmartFieldRenderer';

const GROUPS = {
    pan:    { title: 'PAN DETAILS',  icon: 'pi-id-card', keys: ['pan_number'] },
    gstins: { title: 'GSTIN LIST',   icon: 'pi-list',    keys: ['gstin_list'] },
};

const HIGHLIGHT = ['pan_number'];

// `onDownloadPDF`/`pdfPasswordHint` are optional. The shared-link and lead
// history views render this component without them, and those readers have no
// business regenerating a billed report's PDF — so the button only appears when
// the host actually wires it up.
const GstinByPanOutputComponent = ({ reportData, hideCredits, isSharedView, onDownloadPDF, pdfPasswordHint }) => {
    const shouldHideCredits = hideCredits || isSharedView;
    const { responseData, remainingBalance, errorMsg, hasRealData } = extractResponseData(reportData);

    if (!reportData) return <NoRecordsCard subtitle="No data available" />;
    if (!responseData || !hasRealData) {
        return <NoRecordsCard errorMsg={errorMsg} subtitle="No records found for the provided details. Please verify and try again." />;
    }

    const d = responseData;
    const grouped = {};
    for (const [groupName, group] of Object.entries(GROUPS)) {
        const picked = {};
        group.keys.forEach(k => { if (k in d && d[k] !== null && d[k] !== undefined && d[k] !== '') picked[k] = d[k]; });
        if (Object.keys(picked).length > 0) grouped[groupName] = picked;
    }
    const gstinCount = Array.isArray(d.gstin_list) ? d.gstin_list.length : 0;
    const knownKeys = new Set(Object.values(GROUPS).flatMap(g => g.keys));
    const leftover = {};
    for (const [k, v] of Object.entries(d)) {
        if (SP_SKIP.has(k) || knownKeys.has(k)) continue;
        if (v === null || v === undefined || v === '') continue;
        leftover[k] = v;
    }

    return (
        <div>
            <SmartStyles />
            <SmartHeader title="GSTIN by PAN Report" icon="pi-search" remainingBalance={remainingBalance} shouldHideCredits={shouldHideCredits} />
            <div className="horizontal-report-card">
                {onDownloadPDF && (
                    <>
                        <div className="flex justify-content-end flex-wrap gap-2 mb-2">
                            <Button
                                label="Download PDF"
                                icon="pi pi-file-pdf"
                                size="small"
                                outlined
                                onClick={onDownloadPDF}
                            />
                        </div>
                        {pdfPasswordHint && (
                            <div className="surface-100 border-round border-left-3 border-primary p-2 mb-2 flex align-items-start gap-2">
                                <i className="pi pi-lock text-primary text-sm mt-1" aria-hidden="true" />
                                <span className="text-600 text-sm line-height-3">{pdfPasswordHint}</span>
                            </div>
                        )}
                    </>
                )}
                {/* A PAN with nothing registered against it is still a completed,
                    charged lookup. Saying so plainly beats an empty GSTIN LIST
                    heading that reads like the page failed to load. */}
                {gstinCount === 0 && (
                    <div className="surface-100 border-round border-left-3 border-orange-500 p-2 mb-2 flex align-items-start gap-2">
                        <i className="pi pi-info-circle text-orange-500 text-sm mt-1" aria-hidden="true" />
                        <span className="text-600 text-sm line-height-3">
                            No GSTIN is registered against this PAN. The lookup was performed, so credits were used.
                        </span>
                    </div>
                )}
                {Object.entries(GROUPS).map(([groupName, meta]) => (
                    grouped[groupName]
                        ? <SmartSection key={groupName} title={meta.title} icon={meta.icon} data={grouped[groupName]} highlightKeys={HIGHLIGHT} />
                        : null
                ))}
                {Object.keys(leftover).length > 0 && (
                    <SmartSection title="OTHER DETAILS" icon="pi-info-circle" data={leftover} />
                )}
            </div>
        </div>
    );
};

export default GstinByPanOutputComponent;
